import {
  Button,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Center, useToken } from '@chakra-ui/react';
import { ChevronRight } from 'lucide-react';
import { BlockArchetype, DashboardBlock } from '../../../models/block';
import { isAssignableBlock } from './utils';
import BlockInstructionsEditor from 'components/UserDashboard/overview/BlockInstructionsEditor';
import { useDashboardBlocks } from 'api/healthCoach/useDashboardBlocks';
import { Loader } from 'components/Loader';
import { useUserBlocks } from 'api/healthCoach/useUserBlocks';

type Props = {
  onCreateBlock: (block: DashboardBlock) => void;
  onPatientInstructionsChange: (text: string) => void;
  onSelectBlock: (block: DashboardBlock) => void;
  patientInstructions: string;
  selectedBlock: DashboardBlock;
};

export const BlockCreateContent = ({
  onCreateBlock,
  onPatientInstructionsChange,
  onSelectBlock,
  patientInstructions,
  selectedBlock,
}: Props) => {
  const [secondary200, muted400] = useToken('colors', [
    'secondary.200',
    'muted.400',
  ]);

  const { blocks: userBlocks, isLoading: isUserBlocksLoading } = useUserBlocks({
    blockType: selectedBlock.block_type,
    archived: false,
  });

  const { blocks: blockOptions, isLoading: isDashboardBlocksLoading } =
    useDashboardBlocks({
      blockType: selectedBlock.block_type,
    });

  const activeAssignedBlockStableStrings = userBlocks?.map(
    (b) => b.stable_string
  );

  const hasContraindicatedBlock = userBlocks?.find(
    ({ archetype }) => archetype === BlockArchetype.Contraindicated
  );

  // if selected create block has an id, specific block is selected.
  // Otherwise just a block type has been selected
  const blockSelected = !!selectedBlock.id;

  if (isUserBlocksLoading || isDashboardBlocksLoading)
    return <Loader loading />;

  return (
    <VStack h={'100%'} p={6} justifyContent={'space-between'}>
      {blockSelected ? (
        <>
          <VStack mt={5}>
            {selectedBlock?.archetype === BlockArchetype.Contraindicated ? (
              <Text color={'squash'}>
                {`Please note: assigning this block to the member will archive all active ${selectedBlock?.block_type} blocks and will prevent all ${selectedBlock?.block_type} blocks from surfacing in the member's mobile app.`}
              </Text>
            ) : (
              <BlockInstructionsEditor
                instructions={patientInstructions}
                onChange={onPatientInstructionsChange}
              />
            )}
          </VStack>

          <HStack borderTopWidth={1} borderColor={'cloud.200'} pt={4} gap={3}>
            <Button
              backgroundColor={'secondary.500'}
              flex={1}
              maxW={'1/2'}
              onClick={() => {
                onCreateBlock(selectedBlock);
              }}
            >
              <Text color={'white'} fontSize={'md'} fontWeight={'normal'}>
                Assign
              </Text>
            </Button>
          </HStack>
        </>
      ) : (
        <VStack>
          {!blockOptions.length && (
            <Text color={'blueGray.600'}>No block types available.</Text>
          )}

          {blockOptions.map((block) => {
            const isAlreadyActive = activeAssignedBlockStableStrings.includes(
              block.stable_string
            );
            const isEnabled =
              isAssignableBlock(block) &&
              !hasContraindicatedBlock &&
              !isAlreadyActive;

            return (
              <Pressable
                key={block.id || block.name}
                borderBottomWidth={1}
                borderColor={'cloud.200'}
                disabled={!isEnabled}
                onClick={() => {
                  onSelectBlock(block);
                }}
                py={3}
                w="100%"
              >
                <HStack alignItems={'center'} justifyContent={'space-between'}>
                  <VStack flex={1} pr={4}>
                    <Text
                      color={isEnabled ? 'secondary.500' : 'muted.400'}
                      fontSize={'lg'}
                      fontWeight={'medium'}
                    >
                      {block.name}
                    </Text>

                    <Text
                      color={isEnabled ? 'blueGray.600' : 'muted.400'}
                      fontSize={'sm'}
                      mt={0.5}
                    >
                      {block.archetype}
                    </Text>
                  </VStack>

                  <Center
                    bgColor={'feather.100'}
                    borderColor={'cloud.200'}
                    borderRadius={'full'}
                    borderWidth={1}
                    h={'40px'}
                    w={'40px'}
                  >
                    <ChevronRight
                      color={isEnabled ? secondary200 : muted400}
                      size={18}
                      strokeWidth={2.5}
                    />
                  </Center>
                </HStack>
              </Pressable>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};
